import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Formik, Form, FieldArray } from 'formik'
import * as Yup from 'yup'
import Loader from '../../../components/Loader'
import { TrashIcon } from '../../../assets/svg'
import { getEvent, createApplication } from '../../../actions/participant'
import { setCurrentEvent } from '../../../store/participant/events/slice'
import locationIcon from '../../../assets/img/location-icon.svg'
import calendarIcon from '../../../assets/img/calendar-icon.svg'
import { Section, Label, TextField, AddLink, TextareaField } from './components'
import { formatRange } from '../../../utils/smallHelpers'


const emptyParticipant = { full_name: '', section_number: '' }
const emptySupervisor = {
  full_name: '',
  phone: '',
  position: '',
  academic_title: '',
}

const initialValues = {
  project_title: '',
  project_description: '',
  participants: [{ ...emptyParticipant }],
  supervisors: [{ ...emptySupervisor }],
}

const validationSchema = Yup.object({
  project_title: Yup.string().trim().required('Название работы обязательно'),
  project_description: Yup.string(),
  participants: Yup.array()
    .of(
      Yup.object({
        full_name: Yup.string(),
        section_number: Yup.string(),
      })
    )
    .test(
      'has-valid-participant',
      'Нужен хотя бы один участник',
      (arr) => Array.isArray(arr) && arr.some((p) => (p.full_name || '').trim())
    ),
  supervisors: Yup.array().of(
    Yup.object({
      full_name: Yup.string(),
      phone: Yup.string(),
      position: Yup.string(),
      academic_title: Yup.string(),
    })
  ),
})

const Apply = () => {
  const { id } = useParams()
  const eventId = Number(id)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const event = useSelector((state) => state.participant.events.current)
  const eventLoading = useSelector((state) => state.participant.loading)

  useEffect(() => {
    dispatch(getEvent(eventId))
    return () => {
      dispatch(setCurrentEvent(null))
    }
  }, [dispatch, eventId])

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      project_title: values.project_title.trim(),
      project_description: values.project_description.trim() || null,
      participants: values.participants
        .filter((p) => p.full_name.trim())
        .map((p) => ({
          full_name: p.full_name.trim(),
          section_number: p.section_number.trim() || null,
        })),
      supervisors: values.supervisors
        .filter((s) => s.full_name.trim())
        .map((s) => ({
          full_name: s.full_name.trim(),
          position: s.position.trim() || null,
          academic_title: s.academic_title.trim() || null,
          phone: s.phone.trim() || null,
        })),
    }
    const result = await dispatch(createApplication(eventId, payload))
    setSubmitting(false)
    if (result?.success) {
      navigate('/participant/requests')
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values }) => (
        <Form>
          <section className="flex flex-col gap-[1.5rem] sm:gap-[2rem] lg:gap-[2.5rem] pt-[1rem] lg:pt-[6.5rem] pb-[6rem] lg:pb-[9rem] w-full max-w-[1000px]">
            <div className="hidden lg:block fixed top-0 right-0 left-[24.5rem] z-[5] bg-[#F9FAFB] pt-[1.875rem] pb-[1.5rem] px-[3.125rem]">
              <h1 className="m-0 text-[2em] font-semibold leading-tight">
                Подача заявки
              </h1>
            </div>

            <h1 className="lg:hidden m-0 text-[1.375rem] sm:text-[1.625rem] font-semibold leading-tight">
              Подача заявки
            </h1>

            {eventLoading && !event ? (
              <Loader size="lg" color="rgb(218 35 41)" />
            ) : (
              <>
                {event && (
                  <section className="flex w-full flex-col gap-[0.625rem] rounded-[20px] border border-solid border-[#D9D9D9] bg-white p-[1.25rem] sm:p-[1.5rem] lg:p-[2rem]">
                    <h3 className="m-0 text-[1.125rem] sm:text-[1.25rem] font-semibold text-[#111827] break-words">
                      {event.name}
                    </h3>
                    <div className="flex flex-col gap-[0.312rem] text-[#6B7280]">
                      {event.location && (
                        <div className="flex items-center gap-[0.625rem]">
                          <img src={locationIcon} alt="" className="h-[1.125rem] w-[1.125rem] sm:h-[1.25rem] sm:w-[1.25rem] shrink-0" />
                          <span className="text-[0.875rem] sm:text-[1rem] break-words">{event.location}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-[0.625rem]">
                        <img src={calendarIcon} alt="" className="h-[1.125rem] w-[1.125rem] sm:h-[1.25rem] sm:w-[1.25rem] shrink-0" />
                        <span className="text-[0.875rem] sm:text-[1rem] break-words">{formatRange(event.start_date, event.end_date)}</span>
                      </div>
                    </div>
                  </section>
                )}

                <Section title="О проекте" className="max-w-[680px]">
                  <div className="flex flex-col gap-[0.5rem]">
                    <Label>Введите название вашей работы (проекта)</Label>
                    <TextField name="project_title" placeholder="Пример названия" />
                  </div>
                  <div className="flex flex-col gap-[0.5rem]">
                    <Label>
                      Перечислите оборудование, необходимое для выступления
                    </Label>
                    <TextareaField
                      name="project_description"
                      placeholder="Проектор, стол, микрофон"
                      rows={6}
                    />
                  </div>
                </Section>

                <Section title="Участники">
                  <FieldArray name="participants">
                    {({ push, remove }) => (
                      <>
                        {values.participants.map((_, index) => (
                          <div key={index} className="flex flex-col sm:flex-row sm:items-end gap-[0.75rem] sm:gap-[1.25rem]">
                            <div className="grid w-full flex-1 grid-cols-1 sm:grid-cols-[2fr_1fr] gap-[0.75rem] sm:gap-[1.25rem]">
                              <div className="flex flex-col gap-[0.5rem]">
                                <Label>ФИО участника</Label>
                                <TextField
                                  name={`participants.${index}.full_name`}
                                  placeholder="Иванов Иван Иванович"
                                />
                              </div>
                              <div className="flex flex-col gap-[0.5rem]">
                                <Label>Номер секции</Label>
                                <TextField
                                  name={`participants.${index}.section_number`}
                                  placeholder="12500"
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              aria-label="Удалить участника"
                              className="flex h-[3rem] w-[3rem] sm:h-[3.5rem] sm:w-[3.5rem] shrink-0 items-center justify-center rounded-[10px] border border-solid border-[#D9D9D9] bg-white cursor-pointer hover:border-[#EF4444] self-start sm:self-auto"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        ))}
                        <AddLink onClick={() => push({ ...emptyParticipant })}>
                          Добавить участника
                        </AddLink>
                      </>
                    )}
                  </FieldArray>
                </Section>

                <Section title="Руководители">
                  <FieldArray name="supervisors">
                    {({ push }) => (
                      <>
                        {values.supervisors.map((_, index) => (
                          <div key={index} className="flex flex-col gap-[0.75rem] sm:gap-[1.25rem]">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[0.75rem] sm:gap-[1.25rem]">
                              <div className="flex flex-col gap-[0.5rem]">
                                <Label>ФИО руководителя</Label>
                                <TextField
                                  name={`supervisors.${index}.full_name`}
                                  placeholder="Иванов Иван Иванович"
                                />
                              </div>
                              <div className="flex flex-col gap-[0.5rem]">
                                <Label>Номер телефона</Label>
                                <TextField
                                  name={`supervisors.${index}.phone`}
                                  type="tel"
                                  placeholder="+7 (XXX) XXX - XX - XX"
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[0.75rem] sm:gap-[1.25rem]">
                              <div className="flex flex-col gap-[0.5rem]">
                                <Label>Должность</Label>
                                <TextField
                                  name={`supervisors.${index}.position`}
                                  placeholder="Введите должность"
                                />
                              </div>
                              <div className="flex flex-col gap-[0.5rem]">
                                <Label>Ученое звание</Label>
                                <TextField
                                  name={`supervisors.${index}.academic_title`}
                                  placeholder="Введите ученое звание"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <AddLink onClick={() => push({ ...emptySupervisor })}>
                          Добавить руководителя
                        </AddLink>
                      </>
                    )}
                  </FieldArray>
                </Section>
              </>
            )}

            <div className="fixed bottom-0 right-0 left-0 lg:left-[24.5rem] z-20 flex items-center justify-center sm:justify-end border-t border-solid border-[#D9D9D9] bg-white px-4 sm:px-6 lg:px-[3.125rem] py-[1rem] sm:py-[1.25rem] lg:py-[1.75rem]">
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !values.project_title.trim() ||
                  !values.participants.some((p) => p.full_name.trim())
                }
                className="w-full sm:w-auto sm:min-w-[18rem] lg:w-[21.875rem] rounded-[10px] border-none px-[1.5rem] py-[0.875rem] sm:py-[1rem] text-[0.938rem] sm:text-[1rem] font-medium text-white cursor-pointer transition-colors disabled:cursor-not-allowed disabled:bg-[#D9D9D9] enabled:bg-[#111827] enabled:hover:bg-[#1F2937]"
              >
                Подать заявку
              </button>
            </div>
          </section>
        </Form>
      )}
    </Formik>
  )
}

export default Apply
